(() => {
  const SELECTOR = '[data-irve-directory]';
  const PAGE_SIZE = 12;
  const normalize = (value) => (value || '').toString().normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim();
  const escapeText = (value) => (value || '').toString();

  class EzevenIrveDirectory {
    constructor(root) {
      this.root = root;
      this.options = {
        showPhone: root.dataset.showPhone !== 'false',
        showEmail: root.dataset.showEmail !== 'false',
        showServices: root.dataset.showServices !== 'false',
      };
      this.all = [];
      this.filtered = [];
      this.visibleFiltered = [];
      this.mapDriven = false;
      this.suppressMapUntil = 0;
      this.userLocation = null;
      this.nearbyActive = false;
      this.locationRadius = Number(root.dataset.locationRadius) || 50;
      this.distanceById = new Map();
      this.locationCircle = null;
      this.pageSize = PAGE_SIZE;
      this.map = null;
      this.layer = null;
      this.markerById = new Map();
      this.activeId = null;
      this.nodes = {
        map: root.querySelector('[data-map]'),
        loading: root.querySelector('[data-loading]'),
        search: root.querySelector('[data-search]'),
        region: root.querySelector('[data-region]'),
        qualification: root.querySelector('[data-qualification]'),
        count: root.querySelector('[data-result-count]'),
        resultsTitle: root.querySelector('[data-results-title]'),
        cards: root.querySelector('[data-cards]'),
        empty: root.querySelector('[data-empty]'),
        more: root.querySelector('[data-more]'),
        reset: root.querySelector('[data-reset]'),
        nearby: root.querySelector('[data-nearby]'),
        geoStatus: root.querySelector('[data-geo-status]'),
      };
      this.start();
    }

    async start() {
      try {
        await this.waitForLeaflet();
        const firstUrl = this.root.dataset.dataUrl;
        const chunkCount = Number(this.root.dataset.chunkCount) || 1;
        const dataChunks = await Promise.all(Array.from({ length: chunkCount }, async (_, index) => {
          const suffix = `-${String(index + 1).padStart(2, '0')}.json`;
          const url = index === 0 ? firstUrl : firstUrl.replace(/-\d{2}\.json(?=\?|$)/, suffix);
          const response = await fetch(url, { credentials: 'omit' });
          if (!response.ok) throw new Error(`Données indisponibles (${response.status})`);
          return response.json();
        }));
        this.all = dataChunks.flatMap((chunk) => Array.isArray(chunk.installateurs) ? chunk.installateurs : []);
        this.initMap();
        this.fillRegions();
        this.bind();
        this.applyFilters(false);
      } catch (error) {
        this.nodes.loading.textContent = 'La carte est momentanément indisponible.';
        console.error('[EZEVEN IRVE]', error);
      }
    }

    waitForLeaflet() {
      return new Promise((resolve, reject) => {
        const start = Date.now();
        const poll = () => {
          if (window.L && window.L.markerClusterGroup) return resolve();
          if (Date.now() - start > 12000) return reject(new Error('La bibliothèque cartographique n’a pas pu être chargée.'));
          window.setTimeout(poll, 100);
        };
        poll();
      });
    }

    initMap() {
      this.map = L.map(this.nodes.map, { center: [46.55, 2.45], zoom: 5.6, scrollWheelZoom: true, doubleClickZoom: true, touchZoom: true, dragging: true, minZoom: 4, maxZoom: 18 });
      L.tileLayer('https://{s}.tile.openstreetmap.fr/osmfr/{z}/{x}/{y}.png', {
        attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
        maxZoom: 19,
        subdomains: 'abc',
      }).addTo(this.map);
      this.layer = L.markerClusterGroup({
        chunkedLoading: true,
        chunkInterval: 120,
        chunkDelay: 30,
        maxClusterRadius: 54,
        spiderfyOnMaxZoom: true,
        showCoverageOnHover: false,
        zoomToBoundsOnClick: true,
        disableClusteringAtZoom: 13,
      });
      this.map.addLayer(this.layer);
      this.map.on('movestart zoomstart', () => {
        if (Date.now() >= this.suppressMapUntil) this.mapDriven = true;
      });
      this.map.on('moveend', () => {
        if (Date.now() < this.suppressMapUntil) return;
        if (this.mapDriven) this.updateVisibleResults();
      });
    }

    fillRegions() {
      const regions = [...new Set(this.all.map((item) => item.region).filter(Boolean))].sort((a, b) => a.localeCompare(b, 'fr'));
      regions.forEach((region) => {
        const option = document.createElement('option');
        option.value = region;
        option.textContent = region;
        this.nodes.region.append(option);
      });
    }

    bind() {
      let timer;
      this.nodes.search.addEventListener('input', () => {
        window.clearTimeout(timer);
        timer = window.setTimeout(() => this.applyFilters(true), 180);
      });
      this.nodes.region.addEventListener('change', () => this.applyFilters(true));
      this.nodes.qualification.addEventListener('change', () => this.applyFilters(true));
      this.nodes.reset.addEventListener('click', () => {
        this.nodes.search.value = '';
        this.nodes.region.value = '';
        this.nodes.qualification.value = '';
        this.nearbyActive = false;
        this.userLocation = null;
        this.distanceById.clear();
        this.mapDriven = false;
        this.nodes.nearby?.setAttribute('aria-pressed', 'false');
        this.nodes.nearby?.classList.remove('is-active');
        this.nodes.geoStatus.hidden = true;
        this.nodes.geoStatus.textContent = '';
        this.nodes.resultsTitle.textContent = this.root.dataset.resultsTitle;
        if (this.locationCircle) this.map.removeLayer(this.locationCircle);
        this.locationCircle = null;
        this.pageSize = PAGE_SIZE;
        this.applyFilters(false);
        this.suppressMapUntil = Date.now() + 800;
        this.map.setView([46.55, 2.45], 5.6);
      });
      this.nodes.nearby?.addEventListener('click', () => this.toggleNearby());
      this.nodes.more.addEventListener('click', () => {
        this.pageSize += PAGE_SIZE;
        this.renderCards();
      });
    }

    applyFilters(zoomToResults) {
      const query = normalize(this.nodes.search.value);
      const region = this.nodes.region.value;
      const qualification = this.nodes.qualification.value;
      const matching = this.all.filter((item) => {
        if (region && item.region !== region) return false;
        if (qualification && !(item.qualifications_irve || []).includes(qualification)) return false;
        if (!query) return true;
        const haystack = normalize([item.nom_entreprise, item.ville, item.code_postal, item.departement_nom, item.departement_code, item.region].join(' '));
        return haystack.includes(query);
      });
      this.distanceById.clear();
      this.filtered = matching;
      if (this.nearbyActive && this.userLocation) {
        this.filtered = matching.filter((item) => {
          const distance = this.distanceBetween(this.userLocation, item);
          this.distanceById.set(String(item.id), distance);
          return distance <= this.locationRadius;
        }).sort((a, b) => this.distanceById.get(String(a.id)) - this.distanceById.get(String(b.id)));
      }
      this.pageSize = PAGE_SIZE;
      this.visibleFiltered = this.filtered;
      this.updateResultSummary();
      this.renderMarkers();
      this.renderCards();
      if (zoomToResults && this.filtered.length && (query || region || qualification)) this.zoomTo(this.filtered);
    }

    renderMarkers() {
      this.layer.clearLayers();
      this.markerById.clear();
      const icon = L.divIcon({ className: 'irve-directory__marker-icon', html: '<span class="irve-directory__pin" aria-hidden="true"></span>', iconSize: [28, 28], iconAnchor: [14, 14], popupAnchor: [0, -14] });
      const markers = this.filtered.map((item) => {
        const marker = L.marker([item.latitude, item.longitude], { icon, title: item.nom_entreprise || 'Installateur IRVE' });
        marker.bindPopup(this.popup(item), { maxWidth: 300 });
        marker.on('click', () => this.setActive(item.id));
        this.markerById.set(String(item.id), marker);
        return marker;
      });
      this.layer.addLayers(markers);
      this.nodes.loading.hidden = true;
    }

    popup(item) {
      const box = document.createElement('div');
      const title = document.createElement('p');
      title.className = 'irve-directory__popup-title';
      title.textContent = escapeText(item.nom_entreprise);
      const address = document.createElement('p');
      address.className = 'irve-directory__popup-address';
      address.textContent = `${item.code_postal || ''} ${item.ville || ''} · ${item.departement_nom || ''}`;
      const badges = document.createElement('div');
      badges.className = 'irve-directory__popup-badges';
      (item.qualifications_irve || []).forEach((qualification) => {
        const badge = document.createElement('span');
        badge.className = 'irve-directory__badge';
        badge.textContent = qualification;
        badges.append(badge);
      });
      box.append(title, address, badges);
      if (this.options.showServices && item.services && item.services.length) {
        const services = document.createElement('p');
        services.className = 'irve-directory__popup-services';
        item.services.slice(0, 5).forEach((service) => {
          const tag = document.createElement('span');
          tag.textContent = service;
          services.append(tag);
        });
        box.append(services);
      }
      const contact = document.createElement('div');
      contact.className = 'irve-directory__popup-contact';
      if (this.options.showPhone && (item.telephone_e164 || item.telephone)) {
        const phone = document.createElement('a');
        phone.href = `tel:${item.telephone_e164 || item.telephone.replace(/[^+\d]/g, '')}`;
        phone.textContent = item.telephone || item.telephone_e164;
        contact.append(phone);
      }
      if (this.options.showEmail && item.email) {
        const email = document.createElement('a');
        email.href = `mailto:${item.email}`;
        email.textContent = item.email;
        contact.append(email);
      }
      if (contact.childElementCount) box.append(contact);
      return box;
    }

    renderCards() {
      const visible = this.visibleFiltered.slice(0, this.pageSize);
      const fragment = document.createDocumentFragment();
      visible.forEach((item) => fragment.append(this.card(item)));
      this.nodes.cards.replaceChildren(fragment);
      this.nodes.more.hidden = this.visibleFiltered.length <= this.pageSize;
    }

    card(item) {
      const card = document.createElement('article');
      card.className = 'irve-directory__card';
      card.dataset.id = item.id;
      card.tabIndex = 0;
      card.setAttribute('aria-label', `${item.nom_entreprise}, ${item.code_postal} ${item.ville}`);
      const title = document.createElement('h4');
      title.className = 'irve-directory__card-title';
      title.textContent = escapeText(item.nom_entreprise);
      const location = document.createElement('p');
      location.className = 'irve-directory__card-location';
      location.textContent = `${item.adresse_complete ? `${item.adresse_complete}, ` : ''}${item.code_postal || ''} ${item.ville || ''} · ${item.departement_nom || ''}`;
      const badges = document.createElement('div');
      badges.className = 'irve-directory__badges';
      (item.qualifications_irve || []).forEach((qualification) => {
        const badge = document.createElement('span');
        badge.className = 'irve-directory__badge';
        badge.textContent = qualification;
        badges.append(badge);
      });
      card.append(title, location, badges);
      if (this.nearbyActive && this.distanceById.has(String(item.id))) {
        const distance = document.createElement('p');
        distance.className = 'irve-directory__distance';
        distance.textContent = `${new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 1 }).format(this.distanceById.get(String(item.id)))} km`;
        card.append(distance);
      }
      if (this.options.showServices && item.services && item.services.length) {
        const services = document.createElement('p');
        services.className = 'irve-directory__services';
        item.services.slice(0, 5).forEach((service) => {
          const tag = document.createElement('span');
          tag.textContent = service;
          services.append(tag);
        });
        card.append(services);
      }
      const actions = document.createElement('div');
      actions.className = 'irve-directory__card-actions';
      const contact = document.createElement('div');
      contact.className = 'irve-directory__contact-lines';
      if (this.options.showPhone && (item.telephone_e164 || item.telephone)) {
        const phone = document.createElement('a');
        phone.href = `tel:${item.telephone_e164 || item.telephone.replace(/[^+\d]/g, '')}`;
        phone.textContent = item.telephone || item.telephone_e164;
        phone.setAttribute('aria-label', `Téléphoner à ${item.nom_entreprise}`);
        contact.append(phone);
        actions.append(this.action('Appeler', phone.href, true));
      }
      if (this.options.showEmail && item.email) {
        const email = document.createElement('a');
        email.href = `mailto:${item.email}`;
        email.textContent = item.email;
        email.setAttribute('aria-label', `Envoyer un e-mail à ${item.nom_entreprise}`);
        contact.append(email);
        actions.append(this.action('E-mail', email.href, false));
      }
      if (contact.childElementCount) card.append(contact);
      const route = new URL('https://www.google.com/maps/dir/');
      route.searchParams.set('api', '1');
      route.searchParams.set('destination', `${item.latitude},${item.longitude}`);
      actions.append(this.action('Itinéraire', route.toString(), false, true));
      card.append(actions);
      card.addEventListener('click', (event) => {
        if (event.target.closest('a')) return;
        this.focusMarker(item);
      });
      card.addEventListener('keydown', (event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          this.focusMarker(item);
        }
      });
      return card;
    }

    action(label, href, primary, external = false) {
      const link = document.createElement('a');
      link.className = `irve-directory__action${primary ? ' irve-directory__action--primary' : ''}`;
      link.textContent = label;
      link.href = href;
      if (external) {
        link.target = '_blank';
        link.rel = 'noopener noreferrer';
      }
      return link;
    }

    safeHttps(value) {
      try {
        return new URL(value).protocol === 'https:' ? value : '';
      } catch (_) {
        return '';
      }
    }

    focusMarker(item) {
      const marker = this.markerById.get(String(item.id));
      if (!marker) return;
      this.setActive(item.id);
      this.mapDriven = true;
      this.map.flyTo(marker.getLatLng(), Math.max(this.map.getZoom(), 11), { duration: 0.45 });
      marker.openPopup();
    }

    setActive(id) {
      this.activeId = String(id);
      this.nodes.cards.querySelectorAll('.irve-directory__card').forEach((card) => card.classList.toggle('is-active', card.dataset.id === this.activeId));
      this.markerById.forEach((marker, markerId) => {
        marker.getElement()?.querySelector('.irve-directory__pin')?.classList.toggle('is-active', markerId === this.activeId);
      });
    }

    zoomTo(items) {
      if (!this.map || !items.length) return;
      const bounds = L.latLngBounds(items.map((item) => [item.latitude, item.longitude]));
      if (bounds.isValid()) {
        this.suppressMapUntil = Date.now() + 900;
        this.map.fitBounds(bounds, { padding: [32, 32], maxZoom: 10, animate: false });
      }
    }

    updateResultSummary() {
      const count = this.visibleFiltered.length;
      this.nodes.count.textContent = `${new Intl.NumberFormat('fr-FR').format(count)} résultat${count > 1 ? 's' : ''}`;
      this.nodes.empty.hidden = count !== 0;
      this.nodes.resultsTitle.textContent = this.nearbyActive ? this.root.dataset.nearbyTitle : this.root.dataset.resultsTitle;
    }

    updateVisibleResults() {
      if (this.nearbyActive || !this.mapDriven) {
        this.visibleFiltered = this.filtered;
      } else {
        const bounds = this.map.getBounds();
        this.visibleFiltered = this.filtered.filter((item) => bounds.contains([item.latitude, item.longitude]));
      }
      this.pageSize = PAGE_SIZE;
      this.updateResultSummary();
      this.renderCards();
    }

    toggleNearby() {
      if (!navigator.geolocation) {
        this.nodes.geoStatus.textContent = 'La géolocalisation n’est pas disponible sur cet appareil.';
        this.nodes.geoStatus.hidden = false;
        return;
      }
      if (this.nearbyActive) {
        this.nearbyActive = false;
        this.userLocation = null;
        this.distanceById.clear();
        this.nodes.nearby.setAttribute('aria-pressed', 'false');
        this.nodes.nearby.classList.remove('is-active');
        this.nodes.geoStatus.hidden = true;
        this.nodes.resultsTitle.textContent = this.root.dataset.resultsTitle;
        if (this.locationCircle) this.map.removeLayer(this.locationCircle);
        this.locationCircle = null;
        this.applyFilters(false);
        this.mapDriven = true;
        this.updateVisibleResults();
        return;
      }
      this.nodes.geoStatus.textContent = 'Recherche de votre position…';
      this.nodes.geoStatus.hidden = false;
      navigator.geolocation.getCurrentPosition((position) => {
        this.userLocation = { latitude: position.coords.latitude, longitude: position.coords.longitude };
        this.nearbyActive = true;
        this.mapDriven = false;
        this.nodes.nearby.setAttribute('aria-pressed', 'true');
        this.nodes.nearby.classList.add('is-active');
        this.nodes.geoStatus.textContent = `Dans un rayon de ${this.locationRadius} km`;
        this.applyFilters(false);
        if (this.locationCircle) this.map.removeLayer(this.locationCircle);
        this.locationCircle = L.circle([this.userLocation.latitude, this.userLocation.longitude], {
          radius: this.locationRadius * 1000,
          color: this.root.style.getPropertyValue('--irve-accent-strong').trim(),
          weight: 2,
          fillColor: this.root.style.getPropertyValue('--irve-accent').trim(),
          fillOpacity: 0.08,
        }).addTo(this.map);
        this.suppressMapUntil = Date.now() + 900;
        this.map.setView([this.userLocation.latitude, this.userLocation.longitude], 9);
      }, (error) => {
        const messages = {
          1: 'Autorisez la position dans votre navigateur pour rechercher autour de vous.',
          2: 'Votre position est momentanément indisponible.',
          3: 'La recherche de position a expiré. Réessayez.',
        };
        this.nodes.geoStatus.textContent = messages[error.code] || 'Impossible de déterminer votre position.';
        this.nodes.geoStatus.hidden = false;
      }, { enableHighAccuracy: false, timeout: 10000, maximumAge: 300000 });
    }

    distanceBetween(origin, item) {
      const radians = (degrees) => degrees * Math.PI / 180;
      const earthRadius = 6371;
      const latDelta = radians(item.latitude - origin.latitude);
      const lonDelta = radians(item.longitude - origin.longitude);
      const a = Math.sin(latDelta / 2) ** 2 + Math.cos(radians(origin.latitude)) * Math.cos(radians(item.latitude)) * Math.sin(lonDelta / 2) ** 2;
      return 2 * earthRadius * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    }
  }

  const init = (root) => {
    if (!root || root.dataset.irveInitialized === 'true') return;
    root.dataset.irveInitialized = 'true';
    new EzevenIrveDirectory(root);
  };
  document.querySelectorAll(SELECTOR).forEach(init);
  document.addEventListener('shopify:section:load', (event) => init(event.target.querySelector(SELECTOR)));
})();
